import type { UseQueryResult } from '@/api/hooks';
import { useChangeBrowserTabTitleEffect } from '@/hooks';
import { useMessage } from '@/providers';
import { isPS } from '@/utils';
import { isAxiosError } from 'axios';
import { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useMediaQuery } from 'react-responsive';
import { matchPath, useLocation } from 'react-router';
import {
  BREAK_POINTS,
  PAGE_INFO,
  SITE_NAME,
  SITE_URL,
  VIEW_PORT_WIDTH_FOR_SP,
  setCanonicalUrl,
} from '../utils';

export const useChangeBrowserTabTitleEffectForPS = (props: {
  pageKey: keyof typeof PAGE_INFO;
}) => {
  const { pageKey } = props;
  useChangeBrowserTabTitleEffect({
    title: PAGE_INFO[pageKey].title,
    fallback: SITE_NAME,
  });
};

export const useCurrentPageInfo = () => {
  const location = useLocation();
  const currentPageInfo = useMemo(
    () =>
      Object.entries(PAGE_INFO)
        .filter(([, value]) => {
          return matchPath(value.path, location.pathname);
        })
        .map(([, value]) => value)[0],
    [location.pathname],
  );

  return { currentPageInfo, location };
};

export const useHandle403ErrorWhenSearching = (queryResult: UseQueryResult) => {
  const { openMessage } = useMessage();
  const { t } = useTranslation();

  useEffect(() => {
    if (isPS() && isAxiosError(queryResult.error)) {
      if (queryResult.error.response?.status === 403) {
        openMessage('error', t('error-message-invalid-search-query'));
      }
    }
  }, [openMessage, queryResult.error, t]);
};

export const useManageCanonicalUrl = () => {
  const { currentPageInfo, location } = useCurrentPageInfo();

  useEffect(() => {
    const path = currentPageInfo?.createPath(location.pathname) ?? null;
    if (path) {
      setCanonicalUrl(`${SITE_URL}${path}`);
    }
  }, [currentPageInfo, location.pathname]);
};

export const useManageViewport = () => {
  const { currentPageInfo } = useCurrentPageInfo();

  useEffect(() => {
    const setViewport = () => {
      const meta = document.querySelector('meta[name=viewport]');

      if (currentPageInfo?.shouldForcePcViewport) {
        if (window.innerWidth < BREAK_POINTS.pcMin) {
          meta?.setAttribute('content', `width=${VIEW_PORT_WIDTH_FOR_SP}`);
        }
      } else {
        meta?.setAttribute('content', 'width=device-width, initial-scale=1.0');
      }
    };

    setViewport();

    window.history.replaceState(null, '', null);
    window.addEventListener('popstate', setViewport);
    return () => {
      window.removeEventListener('popstate', setViewport);
    };
  }, [currentPageInfo]);
};

export const useMQ = () => {
  const sp = useMediaQuery({
    query: `(max-width: ${BREAK_POINTS.spMax}px)`,
  });

  const tb = useMediaQuery({
    query: `(min-width: ${BREAK_POINTS.spMax + 1}px)`,
  });

  const pcMin = useMediaQuery({
    query: `(min-width: ${BREAK_POINTS.pcMin}px)`,
  });

  const pcMd = useMediaQuery({
    query: `(min-width: ${BREAK_POINTS.pcMd}px)`,
  });

  return { sp, tb, pcMin, pcMd };
};
